import React, { useState, useEffect, useCallback, useMemo } from "react";

import api from "./api";
import {
  weekStartIsoForIndex,
  datesForWeekOffset,
  PLANNING_WEEK_INDEX_MIN,
  PLANNING_WEEK_INDEX_MAX,
  relativeCalendarWeekLabel,
} from "./planningCalendar";

const days = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

function parseTimeToHours(timeStr) {
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours + minutes / 60;
}

function getShiftHours(shiftString) {
  if (!shiftString || shiftString === "-") return 0;
  return shiftString
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean)
    .reduce((sum, part) => {
      const [start, end] = part.split("-").map((t) => t.trim());
      return sum + (parseTimeToHours(end) - parseTimeToHours(start));
    }, 0);
}

function getShift(shiftsData, employee, day) {
  if (!Array.isArray(shiftsData)) return "-";
  const s = shiftsData.find((shift) => shift.employee === employee && shift.day === day);
  return s ? s.shift : "-";
}

function getActualShift(actualShifts, employee, day) {
  const emp = actualShifts && actualShifts[employee];
  if (!emp) return "";
  return emp[day] || "";
}

function splitShift(shiftString) {
  if (!shiftString || shiftString === "-") {
    return { morning: "", afternoon: "" };
  }
  const parts = shiftString.split("/").map((p) => p.trim());
  if (parts.length === 1) {
    return { morning: parts[0], afternoon: "" };
  }
  return { morning: parts[0], afternoon: parts[1] };
}

function formatDateIso(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/**
 * Une cellule Excel du type ="2026-04-27" affiche la date en texte lisible
 * (évite les ####### quand la colonne est étroite ou le format date ambigu).
 * Champ CSV correctement échappé.
 */
function csvExcelIsoDateField(isoStr) {
  return `"=""${isoStr.replace(/"/g, '""')}"""`;
}

/**
 * Excel sous Windows ouvre souvent les .csv en ANSI : les accents UTF-8 deviennent « EmployÃ© ».
 * UTF-16 LE + BOM est en général interprété correctement.
 */
function csvStringToExcelUnicodeBlob(csvText) {
  const bom = new Uint8Array([0xff, 0xfe]);
  const body = new Uint8Array(csvText.length * 2);
  for (let i = 0; i < csvText.length; i++) {
    const code = csvText.charCodeAt(i);
    body[i * 2] = code & 0xff;
    body[i * 2 + 1] = (code >> 8) & 0xff;
  }
  const out = new Uint8Array(bom.length + body.length);
  out.set(bom, 0);
  out.set(body, bom.length);
  return new Blob([out], { type: "text/csv;charset=utf-16le" });
}

/** Heures réelles pour le rapport : si la semaine est validée par le manager et qu'aucune heure réelle n'est saisie, on reprend le prévu. */
function getReportActualHoursTotal(
  emp,
  days,
  shifts,
  actualShifts,
  employeeDayStatus,
  weekValidated
) {
  return days.reduce((sum, day) => {
    const type = employeeDayStatus[emp]?.[day] ?? "work";
    if (type !== "work") return sum;
    const planned = getShift(shifts, emp, day);
    const rawActual = getActualShift(actualShifts, emp, day);
    const usePlannedAsReal =
      weekValidated && (!rawActual || !String(rawActual).trim());
    const effective = usePlannedAsReal ? planned : rawActual || "";
    return sum + getShiftHours(effective);
  }, 0);
}

function Report({ user, employees, showToast }) {
  const isManager = user?.role === "manager";
  const [weekIndex, setWeekIndex] = useState(0);
  const [shiftsByWeek, setShiftsByWeek] = useState({});
  const [actualShiftsByWeek, setActualShiftsByWeek] = useState({});
  const [employeeDayStatusByWeek, setEmployeeDayStatusByWeek] = useState({});
  const [weeklySubmissionsByWeek, setWeeklySubmissionsByWeek] = useState({});

  const refreshWeekFromApi = useCallback(async (idx) => {
    const data = await api.getPlanning(weekStartIsoForIndex(idx));
    setShiftsByWeek((prev) => ({ ...prev, [idx]: data.shifts }));
    setActualShiftsByWeek((prev) => ({ ...prev, [idx]: data.actualShifts }));
    setEmployeeDayStatusByWeek((prev) => ({ ...prev, [idx]: data.employeeDayStatus }));
    setWeeklySubmissionsByWeek((prev) => ({ ...prev, [idx]: data.weeklySubmissions }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refreshWeekFromApi(weekIndex);
      } catch (err) {
        if (!cancelled) showToast?.(String(err?.message || "Erreur chargement"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [weekIndex, refreshWeekFromApi, showToast]);

  useEffect(() => {
    const onFocus = () => {
      refreshWeekFromApi(weekIndex).catch(() => {});
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [weekIndex, refreshWeekFromApi]);

  const shifts = shiftsByWeek[weekIndex] ?? shiftsByWeek[0] ?? [];
  const actualShifts = actualShiftsByWeek[weekIndex] ?? actualShiftsByWeek[0] ?? {};
  const employeeDayStatus = employeeDayStatusByWeek[weekIndex] ?? employeeDayStatusByWeek[0] ?? {};
  const weeklySubmissions =
    weeklySubmissionsByWeek[weekIndex] ?? weeklySubmissionsByWeek[0] ?? {};

  const currentWeekDates = useMemo(() => datesForWeekOffset(weekIndex), [weekIndex]);
  const fmt = (d) =>
    `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  const start = currentWeekDates[0];
  const end = currentWeekDates[currentWeekDates.length - 1];

  const getEffectiveDayType = (employee, day) => employeeDayStatus[employee]?.[day] ?? "work";

  const handleExportCsv = () => {
    const sep = ";";
    const enc = (s) => (s == null ? "" : String(s).replace(/"/g, '""'));
    const header = [
      "Employé",
      "Date (AAAA-MM-JJ)",
      "Matin prévu",
      "Après-midi prévu",
      "Matin réel",
      "Après-midi réel",
      "Statut",
    ].join(sep);
    const statusLabel = (t) =>
      t === "work" ? "Travail" : t === "off" ? "Repos" : t === "holiday" ? "Jour férié" : "Vacances";
    const rows = [];
    employees.forEach((emp) => {
      const weekValidated = weeklySubmissions[emp] === "manager_validated";
      days.forEach((day, i) => {
        const d = currentWeekDates[i];
        const dateCsv = csvExcelIsoDateField(formatDateIso(d));
        const type = getEffectiveDayType(emp, day);
        const planned = getShift(shifts, emp, day);
        const rawActual = getActualShift(actualShifts, emp, day);
        let mP = "";
        let aP = "";
        let mA = "";
        let aA = "";
        if (type === "work") {
          const sp = splitShift(planned);
          mP = sp.morning;
          aP = sp.afternoon;
          const usePlannedAsReal =
            weekValidated && (!rawActual || !String(rawActual).trim());
          const effectiveActual = usePlannedAsReal ? planned : rawActual || "";
          const sa = splitShift(effectiveActual);
          mA = sa.morning;
          aA = sa.afternoon;
        }
        rows.push(
          [emp, dateCsv, enc(mP), enc(aP), enc(mA), enc(aA), statusLabel(type)].join(sep)
        );
      });
    });
    // `sep=;` : Excel FR sépare bien les colonnes. Fichier en UTF-16 LE pour les accents (voir csvStringToExcelUnicodeBlob).
    const linesep = "\r\n";
    const csv = "sep=;" + linesep + header + linesep + rows.join(linesep);
    const blob = csvStringToExcelUnicodeBlob(csv);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `planning-${weekStartIsoForIndex(weekIndex)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    showToast && showToast("Export CSV téléchargé.");
  };

  if (!isManager) {
    return (
      <div className="page-content">
        <p style={{ color: "#64748b" }}>Cette page est réservée au manager.</p>
      </div>
    );
  }

  return (
    <div className="page-content">
      <h2 style={{ marginTop: 0, fontSize: "clamp(1.25rem, 4vw, 1.6rem)", color: "#0f172a" }}>
        Rapport hebdomadaire
      </h2>
      <p style={{ marginBottom: "20px", color: "#555", fontSize: "clamp(0.85rem, 2vw, 1rem)" }}>
        Comparaison heures prévues / heures réelles pour chaque employé sur la semaine choisie.
        Si la semaine est validée par le manager et qu&apos;aucune heure réelle n&apos;a été saisie,
        le réel reprend le prévu (jours de travail). Un jour peut être en « Travail » sans créneaux
        renseignés si le planning n&apos;est pas encore complété pour ce jour-là.
      </p>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "20px",
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: "clamp(0.85rem, 2vw, 0.9rem)", color: "#0f172a" }}>
          {fmt(start)} – {fmt(end)} · {relativeCalendarWeekLabel(weekIndex)}
        </span>
        <button
          type="button"
          className="touch-target btn-mobile"
          onClick={() => setWeekIndex((prev) => Math.max(PLANNING_WEEK_INDEX_MIN, prev - 1))}
          style={{
            borderRadius: "999px",
            border: "1px solid #cbd5f5",
            background: "white",
            padding: "10px 14px",
            fontSize: "0.85rem",
            cursor: "pointer",
          }}
        >
          ◀ Préc.
        </button>
        <button
          type="button"
          className="touch-target btn-mobile"
          onClick={() => setWeekIndex((prev) => Math.min(PLANNING_WEEK_INDEX_MAX, prev + 1))}
          style={{
            borderRadius: "999px",
            border: "1px solid #cbd5f5",
            background: "white",
            padding: "10px 14px",
            fontSize: "0.85rem",
            cursor: "pointer",
          }}
        >
          Suiv. ▶
        </button>
        <button
          type="button"
          className="touch-target btn-mobile"
          onClick={() => setWeekIndex(0)}
          style={{
            borderRadius: "999px",
            border: "1px solid #0f4dcc",
            background: "white",
            color: "#0f4dcc",
            padding: "10px 14px",
            fontSize: "0.85rem",
            cursor: "pointer",
          }}
        >
          Semaine actuelle
        </button>
        <button
          type="button"
          className="touch-target btn-mobile"
          onClick={handleExportCsv}
          style={{
            borderRadius: "999px",
            border: "1px solid #0f4dcc",
            background: "#0f4dcc",
            color: "white",
            padding: "10px 14px",
            fontSize: "0.85rem",
            cursor: "pointer",
          }}
        >
          Export CSV
        </button>
      </div>

      <div className="report-table-wrap">
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            boxShadow: "0 10px 25px rgba(15,23,42,0.08)",
            border: "1px solid #e2e8f0",
            padding: "14px 16px",
            maxWidth: "900px",
            minWidth: "280px",
          }}
        >
        <table
          style={{
            borderCollapse: "collapse",
            width: "100%",
            fontSize: "0.85rem",
          }}
        >
          <thead>
            <tr style={{ background: "#0f172a", color: "white" }}>
              <th style={{ padding: "8px 12px", textAlign: "left" }}>Employé</th>
              <th style={{ padding: "8px 12px", textAlign: "right" }}>Prévu (h)</th>
              <th style={{ padding: "8px 12px", textAlign: "right" }}>Réel (h)</th>
              <th style={{ padding: "8px 12px", textAlign: "right" }}>Écart (réel - prévu)</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((emp) => {
              const planned = days.reduce((sum, day, idx) => {
                if (getEffectiveDayType(emp, day) !== "work") return sum;
                const s = getShift(shifts, emp, day);
                return sum + getShiftHours(s);
              }, 0);
              const weekValidated = weeklySubmissions[emp] === "manager_validated";
              const actual = getReportActualHoursTotal(
                emp,
                days,
                shifts,
                actualShifts,
                employeeDayStatus,
                weekValidated
              );
              const diff = actual - planned;
              return (
                <tr key={emp}>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e2e8f0",
                      fontWeight: 500,
                    }}
                  >
                    {emp}
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e2e8f0",
                      textAlign: "right",
                    }}
                  >
                    {planned.toFixed(1)}
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e2e8f0",
                      textAlign: "right",
                    }}
                  >
                    {actual.toFixed(1)}
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e2e8f0",
                      textAlign: "right",
                      color:
                        diff > 0.1 ? "#16a34a" : diff < -0.1 ? "#b91c1c" : "#0f172a",
                    }}
                  >
                    {diff.toFixed(1)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

export default Report;
